import { Button } from '@ai-indeed/idp-ui';
import { DownOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@umijs/max';
import { useDebounceFn } from 'ahooks';
import { Table, Tag, Tooltip, message } from 'antd';
import { useMemo, useState } from 'react';
// 获取节点信息接口
import { createNlpNodeTrainingResult } from '../service';

import * as API from '@/pages/CustomCenter/ExtractConfig/components/workflow/services';
import * as NodeAPI from '../service';

import { QUERY_KEYS } from '@/pages/CustomCenter/ExtractConfig/type';

import { Ternary } from '@/components';
import { Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import type { NlpExtractionNodeJobData } from '../../../services/type';
import useAnnotationData from '../hooks/use-annotation-data';
import type { TrainModelTag } from '../types';
import type { TrainStatus } from './types';
import { TrainStatusEnum, trainStatusColorMap, trainStatusMap } from './types';
import { getExtractionCustomNode } from '@/pages/CustomCenter/ExtractConfig/components/workflow/services';
import { nanoid } from 'nanoid';

//模型训练数据 展示表格
const DisplayTable = ({ data }: { data: TrainModelTag[] }) => {
  const columns: ColumnsType<TrainModelTag> = [
    {
      title: '字段',
      dataIndex: 'tag_name',
      width: 100,
    },
    {
      title: 'F1',
      dataIndex: 'f1',
      width: 100,
    },
    {
      title: '召回率',
      dataIndex: 'recall',
      width: 100,
    },
    {
      title: '精确率',
      dataIndex: 'precision',
      width: 100,
    },
  ];

  return (
    <Table
      className="[&_.modern-table-cell]:!bg-white"
      rowKey={(r) => r.tag_name}
      dataSource={data}
      columns={columns}
      pagination={false}
      bordered
      size="small"
      tableLayout="fixed"
    />
  );
};

export const StatusBadge = ({ status }: { status: TrainStatus }) => {
  return (
    <Ternary condition={Boolean(trainStatusMap[status])}>
      <Tag color={trainStatusColorMap[status]}>{trainStatusMap[status]}</Tag>
    </Ternary>
  );
};

const getNodeDataQueryKey = (node_uid: string, template_code: string) => [
  QUERY_KEYS.WORKFLOW_CONFIG,
  API.getExtractionCustomNode.queryKey,
  { node_id: node_uid, template_code },
];

// const getAnnotationQueryKey = (template_code: string, node_uid: string) => [
//   QUERY_KEYS.WORKFLOW_CONFIG,
//   ...NodeAPI.getNlpNodeAnnotationTaskOverview.queryKey({
//     template_code,
//     node_uid,
//   }),
// ];

const UNIQUE_KEY = nanoid();

interface Props {
  node_uid: string;
  template_code: string;
  readonly?: boolean;
}
const TrainCard = (props: Props) => {
  const { node_uid, template_code, readonly } = props;
  const queryClient = useQueryClient();
  const [showDetails, setShowDetails] = useState(false);

  const nodeDataQueryKey = useMemo(
    // 'must be different from the key of the parent query',
    () => [...getNodeDataQueryKey(node_uid, template_code), UNIQUE_KEY],
    [node_uid, template_code],
  );

  const { data: trainData } = useQuery({
    queryFn: () =>
      getExtractionCustomNode({ template_code, node_id: node_uid }),
    queryKey: nodeDataQueryKey,
    select(data) {
      return (data?.job_data as NlpExtractionNodeJobData)?.train_model;
    },
    refetchInterval: (data) => {
      if (data?.train_status === TrainStatusEnum.Training) {
        return 2000;
      }
      return false;
    },
  });

  const { train_status, tags = [] } = trainData || {};

  // TODO 优化hook
  // 节点的数据

  // const data = queryClient.getQueryData(nodeDataQueryKey) as Awaited<
  //   ReturnType<typeof API.getExtractionCustomNode>
  // >;

  // 标注卡片的数据
  //   const annotationQueryKey = useMemo(
  //     () => getAnnotationQueryKey(template_code, node_uid),
  //     [template_code, node_uid],
  //   );
  //
  //   const getAnnotationData = async () => {
  //     const annotationData = (await queryClient.getQueryData(
  //       annotationQueryKey,
  //     )) as Awaited<ReturnType<typeof NodeAPI.getNlpNodeAnnotationTaskOverview>>;
  //
  //     return annotationData?.[0];
  //   };

  const { isLoading: loading, data: annotationData } = useAnnotationData({
    template_code: props.template_code,
    node_uid: props.node_uid,
  });

  const handleTrain = async () => {
    const annotated_count = annotationData?.annotated_count || 0;

    // 判断已标注文件是否大于等于3个
    if (annotated_count < 3) {
      message.warning('已标注文件数量小于3个，无法训练');
      return;
    }

    await createNlpNodeTrainingResult({
      template_code,
      node_uid,
      train: 'start',
    });
    message.success('训练成功');
    queryClient.refetchQueries({
      queryKey: nodeDataQueryKey,
    });
  };

  // const handleStopTrain = async () => {
  //   await createNlpNodeTrainingResult({
  //     template_code,
  //     node_uid,
  //     train: 'stop',
  //   });
  //   message.success('停止成功');
  //   queryClient.refetchQueries({
  //     queryKey: nodeDataQueryKey,
  //   });
  // };

  const { run: handleTrainDebounce } = useDebounceFn(handleTrain, {
    wait: 500,
  });

  return (
    <Form.Item
      label={
        <div className="space-x-2">
          <span>模型训练和测评</span>
          <StatusBadge status={train_status as TrainStatus} />
        </div>
      }
    >
      <div className="flex flex-col space-y-3 bg-[var(--bg-main-color)] p-4 rounded transition hover:shadow ">
        <div className="flex gap-4 justify-between">
          <div className="flex gap-1 flex-1 flex-col items-center bg-white px-3 py-2 rounded">
            <span className="flex font-semibold text-blue-400 text-xl">
              {trainData?.f1 ? `${trainData.f1.toPrecision(4)}` : '--'}
            </span>
            <span className="text-gray-500 text-sm">F1</span>
          </div>
          {/* <div className="flex gap-1 flex-1 flex-col items-center bg-white px-3 py-2 rounded">
            <span className="flex font-semibold text-blue-400 text-xl">
              {trainData?.recall ? '0.${trainData.recall}' : '--'}
            </span>
            <span className="text-gray-500 text-sm">召回率</span>
          </div>
          <div className="flex gap-1 flex-1 flex-col items-center bg-white px-3 py-2 rounded">
            <span className="flex font-semibold text-blue-400 text-xl">
              {trainData?.precision ? '0.${trainData.precision}' : '--'}
            </span>
            <span className="text-gray-500 text-sm">精确率 </span>
          </div> */}
        </div>

        <Ternary
          condition={trainData?.train_status !== TrainStatusEnum.Training}
          falseElement={
            <Button
              type="primary"
              className="w-full"
              // onClick={handleStopTrain}
            >
              训练中, 请等待结果...
            </Button>
          }
        >
          <Tooltip
            placement="top"
            title={
              Boolean((annotationData?.annotated_count || 0) < 3)
                ? '已标注文件数量小于3个，无法训练'
                : ''
            }
          >
            <span>
              <Button
                type="outline1"
                className="w-full !bg-white"
                onClick={handleTrainDebounce}
                disabled={Boolean((annotationData?.annotated_count || 0) < 3)}
              >
                一键训练
              </Button>
            </span>
          </Tooltip>
        </Ternary>

        {showDetails && <DisplayTable data={tags} />}
        <div
          className="flex gap-1 items-center justify-center cursor-pointer text-gray-500 text-sm"
          onClick={() => setShowDetails(!showDetails)}
        >
          指标详情 <DownOutlined rotate={showDetails ? 180 : 0} />
        </div>
      </div>
    </Form.Item>
  );
};

export default TrainCard;
